import { useState } from 'react';
import { format } from 'date-fns';
import type { Checkin, EmotionalResidue, LoopFrequency, Rehearsal } from '../lib/types';
import { completeCheckin, createServerCheckin, getWorkspace } from '../lib/store';

interface Props {
  rehearsals: Rehearsal[];
  onRefresh: () => void;
}

export function CheckinsList({ rehearsals, onRefresh }: Props) {
  const ws = getWorkspace();
  const [active, setActive] = useState<Checkin | null>(null);
  const [form, setForm] = useState({ tolerance: 5, reflection: '', freq: 'weekly' as LoopFrequency });

  const allCheckins = rehearsals.flatMap((r) =>
    (r.checkins || []).map((c) => ({ ...c, rehearsal: r }))
  ).sort((a, b) => (b.completed_at || b.scheduled_for) - (a.completed_at || a.scheduled_for));

  const pending = allCheckins.filter((c) => !c.completed_at);
  const past = allCheckins.filter((c) => c.completed_at);

  async function complete(c: Checkin) {
    const residue: EmotionalResidue = { shame: 3, anxiety: 4, fatigue: 5, self_doubt: 4 }; // placeholder; in real would have sliders

    const updates: Partial<Checkin> = {
      completed_at: Date.now(),
      tolerance_now: form.tolerance,
      loop_frequency_now: form.freq,
      reflection: form.reflection.trim() || undefined,
      residue_now: residue,
    };

    completeCheckin(c.id, updates);

    if (ws.consent.allow_server_storage) {
      await createServerCheckin({ ...c, ...updates } as Checkin, ws.id);
    }

    setActive(null);
    setForm({ tolerance: 5, reflection: '', freq: 'weekly' });
    onRefresh();
  }

  if (allCheckins.length === 0) {
    return (
      <div className="text-sm text-[var(--text-muted)] py-6">
        No follow-up prompts yet. When you save a rehearsal with follow-ups enabled, Workflows will schedule private check-ins (visible only here) for 3, 7, and 21 days later.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {pending.length > 0 && (
        <div>
          <div className="uppercase tracking-widest text-xs text-[var(--text-subtle)] mb-3">Waiting for you (no rush)</div>
          <div className="space-y-3">
            {pending.map((c: any) => (
              <div key={c.id} className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--bg-elev)] p-5">
                <div className="text-sm">Check-in for rehearsal on {format(new Date(c.rehearsal.created_at), 'PPP')}</div>
                <div className="text-xs text-[var(--text-subtle)] mt-1">Scheduled ~{c.days_since} days later</div>
                <button onClick={() => setActive(c)} className="mt-4 text-sm underline">Complete this check-in</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <div className="uppercase tracking-widest text-xs text-[var(--text-subtle)] mb-3">Past check-ins</div>
          <div className="space-y-2 text-sm">
            {past.map((c: any) => (
              <div key={c.id} className="rounded-xl border border-[var(--border)] px-4 py-3 flex justify-between items-center">
                <div>
                  {format(new Date(c.completed_at!), 'PP')} • tolerance now {c.tolerance_now}
                </div>
                <div className="text-[var(--text-subtle)] text-xs">{c.reflection ? 'has note' : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setActive(null)}>
          <div className="bg-[var(--bg)] w-full sm:w-[460px] rounded-t-3xl sm:rounded-3xl p-6 border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
            <div className="font-medium mb-4">Private check-in</div>
            <div className="text-sm text-[var(--text-muted)] mb-5">How is the residue and loop frequency now, compared with right after the rehearsal?</div>

            <div className="mb-5">
              <div className="text-xs mb-1.5">Current tolerance for being seen as wrong (1-10)</div>
              <input type="range" min={1} max={10} value={form.tolerance} onChange={(e) => setForm({ ...form, tolerance: parseInt(e.target.value) })} className="w-full accent-[var(--accent)]" />
            </div>

            <div className="mb-5">
              <div className="text-xs mb-1.5">How often is the loop visiting lately?</div>
              <select value={form.freq} onChange={(e) => setForm({ ...form, freq: e.target.value as any })} className="w-full border border-[var(--border)] rounded-xl px-3 py-2 bg-[var(--bg-elev)]">
                <option value="rare">Rarely</option>
                <option value="weekly">Weekly</option>
                <option value="few_per_week">Several times a week</option>
                <option value="daily">Daily</option>
                <option value="multiple_daily">Many times a day</option>
              </select>
            </div>

            <textarea
              value={form.reflection}
              onChange={(e) => setForm({ ...form, reflection: e.target.value })}
              placeholder="Anything you notice about the fear or the move you chose?"
              className="w-full min-h-[90px] border border-[var(--border)] rounded-2xl p-4 text-sm"
            />

            <div className="flex gap-3 mt-5">
              <button onClick={() => complete(active)} className="flex-1 rounded-2xl bg-[var(--accent)] text-white py-2.5">Save check-in</button>
              <button onClick={() => setActive(null)} className="flex-1 rounded-2xl border py-2.5">Later</button>
            </div>
            <div className="text-[10px] text-center text-[var(--text-subtle)] mt-4">This updates your curve and stays completely private to you.</div>
          </div>
        </div>
      )}
    </div>
  );
}
